import {
  BACKDROP_ANIMATION_CLASS_NAME,
  createAnimationStyle,
  createBackdrop,
  createComponentDom,
  getAnimationClassName,
} from "@shared/common/component-manager/element";
import { closeForSeconds } from "@shared/common/component-manager/expiration";
import {
  getLocalData,
  setLocalData,
} from "@shared/common/component-manager/local-storage";
import type {
  ComponentStatus,
  IComponentOption,
  Message,
} from "@shared/common/component-manager/type";
import {
  getOS,
  isMessageEvent as isButtrMessage,
  postMessage,
} from "@shared/common/component-manager/util";
import { FIXED_POPUP_PADDING } from "@shared/common/node/style-data";
import { getPositionStyle } from "@shared/common/node/style-position";
import type { IDocumentNode, PartialNode } from "@shared/common/node/type";
import { getUnitStyle } from "@shared/common/node/util";
import type {
  CampaignType,
  ICampaignFrequencyCappingHandler,
  ICampaignTriggerEvent,
} from "@shared/common/types/campaign";
import type { Data } from "@shared/common/types/data-source";
import type {
  AnalyticsEvent,
  DataJsonMapState,
  IAnalyticsManager,
  IReactionEvent,
} from "@shared/common/types/sdk";
import { getAnalyticsCampaignsType } from "@shared/common/utils/get-analytics-campaign-type";
import { getZIndex } from "@shared/common/utils/helper";
import mergeVideoData from "@shared/common/utils/merge-video-data";
import type { Properties as CSSProperties } from "csstype";
import type { StoreApi } from "zustand/vanilla";
import type { UserData } from "./data";
import type { State } from "./state";
import type { IRecordEventQueue } from "./type";
interface IComponent {
  id: string;
  campaignPublicId: string;
  targetDom: Element;
  iframe: HTMLIFrameElement;
  wrapper: HTMLDivElement;
  container: HTMLDivElement;
  backdrop: HTMLDivElement | null;
  status: ComponentStatus;
  triggered: boolean;
  options: IComponentOption;
  documentNode: PartialNode<IDocumentNode>;
  wrapperStyle: CSSProperties;
  containerStyle: CSSProperties;
  __eventQueue: Message[];
  hasBodyMarginTop?: boolean;
  sizeQueued?: { width: number; height: number } | null;
  trigger: ICampaignTriggerEvent[];
  lazyBodyStyle?: { marginTop: number };
  campaignType: string;
  frequencyCapping?: ICampaignFrequencyCappingHandler | null;
  reset: () => void;
  /**
   * manager.__removeComponent 호출 용도.
   * 삭제시 필요한 작업들은 reset에서 처리할 것
   */
  remove: () => void;
  noCloseForSeconds?: boolean;
}

interface IChildComponent {
  id: string;
  campaignPublicId: string;
  parentPublicId: string;
  campaignType: string;
  documentNode: PartialNode<IDocumentNode>;
  slot: number;
}

export const getComponentId = (siteId: string, campaignId: string) => {
  return `${siteId}-${campaignId}`;
};

export const getDeviceFromWindow = () => {
  if (getOS() !== "Desktop") {
    return "MOBILE";
  }

  const { innerWidth } = window;
  if (innerWidth > 100 && innerWidth < 430) {
    return "MOBILE";
  }

  return "DESKTOP";
};

/**
 *  setBreakpoint에서 설정한 기준값을 우선순위로 판단합니다.
 *  그 다음 기존 user-agent와 window.innerWidth를 기준으로 판단합니다
 */
export const getDevice = () => {
  if (window.CodenButter?.breakPoint) {
    const deviceWidth =
      // innerWidth: 브라우저 창의 너비 (스크롤바 포함)
      // document.documentElement.clientWidth: 브라우저 창의 너비 (스크롤바 제외)
      // 모바일 디바이스에서 특정 사이트 초기 로딩시 innerWidth가 실제 디바이스 가로사이즈보다 커져서 pc 캠페인이 노출되는 경우가 있음
      // 이를 방지하고자 documentElement.clientWidth를 사용하되, 영향도를 최소화하기 위해 크기 차이가 100px 이상 나는 경우에만 documentElement.clientWidth를 사용하도록 함
      // @see https://app.asana.com/0/0/1212902006285826
      window.innerWidth > document.documentElement.clientWidth + 100
        ? document.documentElement.clientWidth
        : window.innerWidth;

    return deviceWidth < window.CodenButter.breakPoint.width
      ? "MOBILE"
      : "DESKTOP";
  }

  return getDeviceFromWindow();
};

export const getContainerStyle = (
  documentNode: PartialNode<IDocumentNode>
): CSSProperties => {
  return {
    // 하단 팝업일 때 top: 0 이면 아래로 스크롤할 때 팝업이 잠시 아래로 내려갔다가 올라옴, 상단 팝업일 때 bottom: 0인 경우도 마찬가지
    ...(documentNode.position?.includes("BOTTOM") ? { bottom: 0 } : { top: 0 }),
    height: window.CSS?.supports?.("height: 100dvh")
      ? "100dvh"
      : "calc(var(--vh, 1vh) * 100)",
    width: "100%",
    position: "fixed",
    left: 0,
    zIndex:
      documentNode.zIndex ??
      getZIndex({ group: "sdkJsBody", name: "component" }),
    pointerEvents: "none",
    backgroundColor: "transparent",
    boxShadow: "none",
    borderRadius: "0",
  };
};

export const getComponentStyle = (
  documentNode: PartialNode<IDocumentNode>
): CSSProperties => {
  return {
    position: "absolute",
    lineHeight: "0", // 내부 iframe에 하단 간격이 생기는 것 방지
    margin: documentNode.optimizeSize
      ? 0
      : getUnitStyle({
          unit: FIXED_POPUP_PADDING.unit,
          value: -FIXED_POPUP_PADDING.value,
        }),
    width: documentNode.width
      ? documentNode.optimizeSize
        ? getUnitStyle(documentNode.width)
        : `calc(${getUnitStyle(documentNode.width)} + ${getUnitStyle({
            unit: FIXED_POPUP_PADDING.unit,
            value: FIXED_POPUP_PADDING.value * 2,
          })})`
      : "1px",
    ...(documentNode.maxWidth && {
      maxWidth: documentNode.optimizeSize
        ? getUnitStyle(documentNode.maxWidth)
        : `calc(${getUnitStyle(documentNode.maxWidth)} + ${getUnitStyle({
            unit: FIXED_POPUP_PADDING.unit,
            value: FIXED_POPUP_PADDING.value * 2,
          })})`,
    }),
    backgroundColor: "transparent",
    colorScheme: "normal", // https://stackoverflow.com/questions/69591128/chrome-is-forcing-a-white-background-on-frames-only-on-some-websites
    boxShadow: "none",
    borderRadius: "0",
  };
};

export const getZoomValue = (component: IComponent) => {
  let ele = component.iframe.parentElement;
  let zoomValue = 1;

  while (ele !== null) {
    // zoom은 표준 속성이 아니라 타입이 존재하지 않음
    const parentStyle = getComputedStyle(ele) as CSSStyleDeclaration & {
      zoom: string;
    };
    zoomValue *= Number(parentStyle.zoom);
    ele = ele.parentElement;
  }

  return String(zoomValue);
};

export class Manager {
  private __allowOrigin: string[] = [];
  private __components: IComponent[] = [];
  private __childComponents: IChildComponent[] = [];
  private __bindedReceiveMessage?: (event: MessageEvent) => void;
  private __unsubscribeStores?: () => void;
  private __userData: UserData;
  private __customData: StoreApi<{ [key: string]: unknown }>;
  private __dataMap: StoreApi<DataJsonMapState>;
  /** @depredated */
  private __ignoreTrack = false;
  private __analyticsManager: IAnalyticsManager;
  private __recordEventQueue: IRecordEventQueue | undefined;
  private __resizeObserver: ResizeObserver | null = null;

  private __siteId = "";

  constructor({
    userData,
    customData,
    dataMap,
    analyticsManager,
  }: {
    userData: UserData;
    customData: StoreApi<{ [key: string]: unknown }>;
    dataMap: StoreApi<DataJsonMapState>;
    analyticsManager: IAnalyticsManager;
  }) {
    this.__userData = userData;
    this.__customData = customData;
    this.__dataMap = dataMap;
    this.__analyticsManager = analyticsManager;
  }

  // TODO: 간소화
  getCurrentValue = <T>(state: State<T>) => {
    const current = state.getState();
    if (current.type === "ready") {
      return current.data;
    }
    return null;
  };

  init({ siteId }: { siteId: string }) {
    this.__siteId = siteId;

    // TODO: 간소화하게 수정
    const isLoginSubscribe = this.__userData.isLogin.subscribe((isLogin) => {
      if (isLogin.type !== "ready") {
        return;
      }

      this.getComponents().forEach((c) => {
        this.send(c.id, {
          type: "UPDATE_DATA",
          user: {
            isLogin: isLogin.data,
            id: this.getCurrentValue(this.__userData.loginId),
            cart: this.getCurrentValue(this.__userData.cart),
            wishlistCount: this.getCurrentValue(this.__userData.wishlistCount),
            coupon: this.getCurrentValue(this.__userData.coupon),
            name: this.getCurrentValue(this.__userData.name),
            email: this.getCurrentValue(this.__userData.email),
          },
        });
      });
    });

    // TODO: 간소화하게 수정
    const loginIdSubscribe = this.__userData.loginId.subscribe((loginId) => {
      if (loginId.type !== "ready") {
        return;
      }

      this.getComponents().forEach((c) => {
        this.send(c.id, {
          type: "UPDATE_DATA",
          user: {
            isLogin: this.getCurrentValue(this.__userData.isLogin),
            id: loginId.data,
            cart: this.getCurrentValue(this.__userData.cart),
            wishlistCount: this.getCurrentValue(this.__userData.wishlistCount),
            coupon: this.getCurrentValue(this.__userData.coupon),
            name: this.getCurrentValue(this.__userData.name),
            email: this.getCurrentValue(this.__userData.email),
          },
        });
      });
    });

    const nameSubscribe = this.__userData.name.subscribe((name) => {
      if (name.type !== "ready") {
        return;
      }

      this.getComponents().forEach((c) => {
        this.send(c.id, {
          type: "UPDATE_DATA",
          user: {
            isLogin: this.getCurrentValue(this.__userData.isLogin),
            id: this.getCurrentValue(this.__userData.loginId),
            cart: this.getCurrentValue(this.__userData.cart),
            wishlistCount: this.getCurrentValue(this.__userData.wishlistCount),
            coupon: this.getCurrentValue(this.__userData.coupon),
            name: name.data,
            email: this.getCurrentValue(this.__userData.email),
          },
        });
      });
    });

    const emailSubscribe = this.__userData.email.subscribe((email) => {
      if (email.type !== "ready") {
        return;
      }
      this.getComponents().forEach((c) => {
        this.send(c.id, {
          type: "UPDATE_DATA",
          user: {
            isLogin: this.getCurrentValue(this.__userData.isLogin),
            id: this.getCurrentValue(this.__userData.loginId),
            cart: this.getCurrentValue(this.__userData.cart),
            wishlistCount: this.getCurrentValue(this.__userData.wishlistCount),
            coupon: this.getCurrentValue(this.__userData.coupon),
            name: this.getCurrentValue(this.__userData.name),
            email: email.data,
          },
        });
      });
    });

    // TODO: 간소화하게 수정
    const cartSubscribe = this.__userData.cart.subscribe((cart) => {
      if (cart.type !== "ready") {
        return;
      }

      this.getComponents().forEach((c) => {
        this.send(c.id, {
          type: "UPDATE_DATA",
          user: {
            isLogin: this.getCurrentValue(this.__userData.isLogin),
            id: this.getCurrentValue(this.__userData.loginId),
            cart: cart.data,
            wishlistCount: this.getCurrentValue(this.__userData.wishlistCount),
            coupon: this.getCurrentValue(this.__userData.coupon),
            name: this.getCurrentValue(this.__userData.name),
            email: this.getCurrentValue(this.__userData.email),
          },
        });
      });
    });

    // TODO: 간소화하게 수정
    const wishlistCountSubscribe = this.__userData.wishlistCount.subscribe(
      (wishlistCount) => {
        if (wishlistCount.type !== "ready") {
          return;
        }

        this.getComponents().forEach((c) => {
          this.send(c.id, {
            type: "UPDATE_DATA",
            user: {
              isLogin: this.getCurrentValue(this.__userData.isLogin),
              id: this.getCurrentValue(this.__userData.loginId),
              cart: this.getCurrentValue(this.__userData.cart),
              wishlistCount: wishlistCount.data,
              coupon: this.getCurrentValue(this.__userData.coupon),
              name: this.getCurrentValue(this.__userData.name),
              email: this.getCurrentValue(this.__userData.email),
            },
          });
        });
      }
    );

    // TODO: 간소화하게 수정
    const couponSubscribe = this.__userData.coupon.subscribe((coupon) => {
      if (coupon.type !== "ready") {
        return;
      }

      this.getComponents().forEach((c) => {
        this.send(c.id, {
          type: "UPDATE_DATA",
          user: {
            isLogin: this.getCurrentValue(this.__userData.isLogin),
            id: this.getCurrentValue(this.__userData.loginId),
            cart: this.getCurrentValue(this.__userData.cart),
            wishlistCount: this.getCurrentValue(this.__userData.wishlistCount),
            coupon: coupon.data,
            name: this.getCurrentValue(this.__userData.name),
            email: this.getCurrentValue(this.__userData.email),
          },
        });
      });
    });

    // TODO: 간소화하게 수정
    const userUnsubscribe = () => {
      isLoginSubscribe();
      loginIdSubscribe();
      nameSubscribe();
      emailSubscribe();
      cartSubscribe();
      wishlistCountSubscribe();
      couponSubscribe();
    };

    const dataUnsubscribe = this.__customData.subscribe((customData) => {
      this.getComponents().forEach((c) => {
        this.send(c.id, {
          type: "UPDATE_DATA",
          customData,
        });
      });
    });

    const dataMapUnsubscribe = this.__dataMap.subscribe((dataMap) => {
      this.getComponents().forEach((c) => {
        this.send(c.id, {
          type: "UPDATE_DATA",
          dataMap: dataMap.map[c.campaignPublicId],
        });
      });
    });

    this.__unsubscribeStores = () => {
      userUnsubscribe();
      dataUnsubscribe();
      dataMapUnsubscribe();
    };

    // window에 함수를 바인드 하면서 this가 바뀌는 것을 방지 (cleanUp때 제거할 함수를 저장)
    this.__bindedReceiveMessage = this.__receiveMessage.bind(this);
    window.addEventListener("message", this.__bindedReceiveMessage, false);

    this.__setResizeObserver(this.__notifyZoomValue.bind(this));
    this.__setResizeHandler(this.__polyfillVdh.bind(this));
    this.__polyfillVdh();
  }

  /** @depredated */
  setIgnoreTrack(ignoreTrack: boolean) {
    this.__ignoreTrack = ignoreTrack;
  }

  setRecordEventQueue(recordEventQueue: IRecordEventQueue | undefined) {
    this.__recordEventQueue = recordEventQueue;
  }

  private __sendMessage(recipientWindow: Window, message: Message) {
    if (this.__recordEventQueue) {
      // rrweb은 기본적으로 iframe을 지원하지 않음
      // 기존 manager가 통신하는 방식을 재현하기 위해 큐에 메시지를 담음
      this.__recordEventQueue.addEvent({
        type: 6,
        data: {
          plugin: "post-message",
          payload: {
            type: "custom",
            data: {
              tag: "post-message",
              payload: {
                message,
              },
            },
          },
        },
        timestamp: Date.now(),
      });
    }
    postMessage(recipientWindow, message);
  }

  private __receiveMessage(event: MessageEvent) {
    const message = event.data;
    if (!message || !isButtrMessage(message)) {
      return;
    }

    if (this.__isOriginAllowed(event.origin)) {
      this.__handleMessage(message);
    }
  }

  private __handleMessage(message: Message) {
    const component = this.getComponents().find((c) => c.id === message.id);
    const childComponents = this.getChildComponents().filter(
      (c) => getComponentId(this.__siteId, c.parentPublicId) === message.id
    );

    if (message.type === "SYN") {
      this.getComponents()
        .filter((c) => c.status === "INITIALIZING")
        .forEach((c) => {
          const iframeWindow = c.iframe.contentWindow;
          if (iframeWindow) {
            this.__sendMessage(iframeWindow, { type: "SYN_ACK", id: c.id });
          }
        });
      return;
    }

    if (!component) {
      // eslint-disable-next-line no-console
      console.error("Component Manager handleMessage: component not found", {
        message,
      });
      return;
    }

    const showDom = () => {
      const {
        iframe,
        wrapper,
        wrapperStyle,
        containerStyle,
        container,
        backdrop,
        targetDom,
        lazyBodyStyle,
      } = component;
      component.status = "ACTIVE";

      const iframeWindow = iframe.contentWindow;
      if (iframeWindow) {
        this.__sendMessage(iframeWindow, {
          type: "SHOW_DOM",
          id: component.id,
        });
      }

      const { width: resizedIframeWidth, height: resizedIframeHeight } =
        iframe.style;

      /** 스타일 적용 */
      iframe.classList.add(getAnimationClassName(component.id));
      document.body.style.marginTop = `${lazyBodyStyle?.marginTop}px`;

      Object.assign(iframe.style, {
        border: 0,
        background: "transparent",
        width: resizedIframeWidth || "100%",
        height: resizedIframeHeight || "100%",
        pointerEvents: "auto",
        position: "static",
      } satisfies CSSProperties);

      container.removeAttribute("style");
      Object.assign(container.style, containerStyle);

      Object.assign(wrapper.style, wrapperStyle);

      // 포지션 설정
      if (component.sizeQueued) {
        this.setPosition(component.id, component.sizeQueued);
        component.sizeQueued = null;
      }

      if (backdrop) {
        if (container.parentNode === targetDom) {
          // 컴포넌트 이전 위치에 렌더링 (z-index가 동일하므로 순서가 중요)
          targetDom.insertBefore(backdrop, container);
        } else {
          targetDom.appendChild(backdrop);
        }

        backdrop.classList.add(BACKDROP_ANIMATION_CLASS_NAME);
      }

      // 닫기 트리거 감지 시작
      const trigger = component.trigger.filter(
        (t) => t.type === "LAZY_CLOSE" || t.type === "SCROLL_CLOSE"
      );
      trigger.forEach((t) => {
        t.addTriggerCallback(() => {
          if (!component.triggered) {
            return;
          }

          // 트리거에 의해 닫기 동작 시 애니메이션 추가
          component.iframe.style.opacity = "0";
          component.iframe.style.transition = "opacity 0.5s ease-out";
          if (component.backdrop) {
            component.backdrop.style.opacity = "0";
            component.backdrop.style.transition = "opacity 0.5s ease-out";
          }

          setTimeout(() => component.remove(), 500);
        });
      });
    };

    const showDomIfReadyAndTriggered = () => {
      if (component.status === "READY" && component.triggered) {
        if (!component.frequencyCapping) {
          showDom();
        } else {
          component.frequencyCapping?.wrap(() => {
            showDom();
          });
        }
      }
    };

    switch (message.type) {
      case "ACK":
        if (message.ready) {
          if (component.status === "LOADING") {
            break;
          }

          // 트리거 감지 시작
          const trigger = component.trigger.filter(
            (t) =>
              t.type === "LAZY" ||
              t.type === "SCROLL" ||
              t.type === "EXIT_INTENT"
          );
          if (trigger.length === 0) {
            if (component.triggered) {
              return;
            }
            component.triggered = true;
            showDomIfReadyAndTriggered();
          } else {
            trigger.forEach((t) => {
              t.addTriggerCallback(() => {
                if (component.triggered) {
                  return;
                }
                component.triggered = true;
                showDomIfReadyAndTriggered();
              });
            });
          }

          // 데이터 전송
          const childDataMap = childComponents.map(
            (component) =>
              this.__dataMap.getState().map[component.campaignPublicId]
          );

          let mergedDataMap: Record</*alias */ string, Data> = {
            ...this.__dataMap.getState().map[component.campaignPublicId],
          };

          childDataMap.forEach((dataMap) => {
            mergedDataMap = {
              ...mergedDataMap,
              ...dataMap,
            };
          });

          mergedDataMap = {
            ...mergedDataMap,
            ...mergeVideoData([
              this.__dataMap.getState().map[component.campaignPublicId],
              ...childDataMap,
            ]),
          };

          component.status = "LOADING";
          this.__sendUnsentEvent(component);
          this.send(component.id, {
            type: "UPDATE_DATA",
            device: getDevice(),
            origin: window.origin,
            data: component.documentNode,
            childrenData: childComponents
              .filter((c) => c.parentPublicId === component.campaignPublicId)
              .sort((a, b) => a.slot - b.slot)
              .map((component) => component.documentNode),
            user: {
              isLogin: this.getCurrentValue(this.__userData.isLogin),
              id: this.getCurrentValue(this.__userData.loginId),
              cart: this.getCurrentValue(this.__userData.cart),
              wishlistCount: this.getCurrentValue(
                this.__userData.wishlistCount
              ),
              coupon: this.getCurrentValue(this.__userData.coupon),
              name: this.getCurrentValue(this.__userData.name),
              email: this.getCurrentValue(this.__userData.email),
            },
            customData: this.__customData.getState(),
            dataMap: {
              ...mergedDataMap,
            },
          });
        } else {
          this.__removeComponent(component.id);
        }
        break;

      case "COMPONENT_RESIZE": {
        // 컴포넌트 내부에서 윈도우 변경 이벤트를 받으면 그에 따라 iframe 사이즈 변경
        const size = component.options.size;
        switch (size.type) {
          case "FIT":
            component.iframe.style.height = `${message.height}px`;
            break;
          case "DEFAULT":
            component.iframe.style.width = size.width;
            component.iframe.style.height = size.height;
            break;
        }

        // 상단 여백 기능 처리
        // TODO: 기존에 이미 상단 여백이 있는 경우 고려
        if (
          component.documentNode.bodyMarginTop?.type === "DOCUMENT_NODE_HEIGHT"
        ) {
          let height = message.height;
          if (!component.documentNode.optimizeSize) {
            height -= 2 * FIXED_POPUP_PADDING.value;
          }

          component.hasBodyMarginTop = true;

          // ACTIVE라면 바로 적용
          // 그렇지 않다면 lazyBodyStyle에 저장 후 트리거 + 피로도확인 후 적용
          if (component.status === "ACTIVE") {
            document.body.style.marginTop = `${height}px`;
          } else {
            component.lazyBodyStyle = { marginTop: height };
          }
        }

        // top, left 위치 값 변경을 위한 데이터 저장
        if (component.options.size.type === "FIT") {
          if (
            component.status === "INITIALIZING" ||
            component.status === "LOADING"
          ) {
            component.sizeQueued = {
              width: message.width,
              height: message.height,
            };
          } else {
            this.setPosition(component.id, {
              width: message.width,
              height: message.height,
            });
          }
        } else {
          // TODO: 가로세로 고정 팝업일 경우 사이즈 무조건 적용
        }
        break;
      }

      case "FORCE_LOAD":
      case "LOAD": {
        if (component.status === "LOADING") {
          component.status = "READY";
          showDomIfReadyAndTriggered();
        }
        break;
      }

      case "OPEN_URL":
        window.open(message.url, message.target);
        break;

      case "EVAL_SCRIPT":
        // eval() 함수로 자유롭게 하고 싶으나 보안이슈로 최대한 보수적으로 접근함
        // 추후 자유로운 함수를 실행하려면 namespace 개념을 도입하여 아무 함수나 실행하는건 막는게 좋을듯
        if (message.action === "AIRBRIDGE:openDeeplink") {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ((window as any).airbridge) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (window as any).airbridge.openDeeplink(message.params);
          }
        }

        if (message.action === "JS") {
          // TODO: 타입을 좀 더 신경쓰기
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          window.eval((message.params as any).js);
        }

        break;

      case "CLOSE":
        component.remove();
        break;

      case "CLOSE_FOR_SECONDS":
        if (component.noCloseForSeconds) {
          component.remove();
          break;
        }

        try {
          const localData = getLocalData();
          const newLocalData = closeForSeconds({
            localData,
            componentId: component.id,
            seconds: message.seconds,
          });
          setLocalData(newLocalData);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(
            "[codenbutter-manager] 브라우저 설정으로 인해 일정 시간 닫기 기능을 사용할 수 없습니다."
          );
        }

        component.remove();
        break;

      case "GO_BACK":
        // IE 10 이상 지원됨
        window.history.back();
        break;

      case "COLLECT_EVENT": {
        if (!message.id) {
          // eslint-disable-next-line no-console
          console.error("[codenbutter] COLLECT_EVENT: id가 없습니다.");
          return;
        }

        if (!message.data.documentNode || !message.data.documentNode.id) {
          // eslint-disable-next-line no-console
          console.error(
            "[codenbutter] COLLECT_EVENT: documentNode가 없습니다."
          );
          return;
        }

        const component =
          this.getComponentByDocumentNodeId(message.data.documentNode.id) ??
          this.getChildComponentByDocumentNodeId(message.data.documentNode.id);

        if (!component) {
          // eslint-disable-next-line no-console
          console.error(
            `[codenbutter] COLLECT_EVENT: ${message.id} 에 해당하는 component가 없습니다.`
          );
          return;
        }

        const campaignType = getAnalyticsCampaignsType(
          component.campaignType,
          this.isChildComponent(component)
        );

        if (!campaignType) {
          // eslint-disable-next-line no-console
          console.error(
            `[codenbutter] COLLECT_EVENT: ${component.campaignType} 에 해당하는 campaignType이 없습니다.`
          );
          return;
        }

        const analyticsEvent: IReactionEvent = {
          type: "REACTION",
          campaignType,
          data: message.data,
          campaignId: component.campaignPublicId,
          siteId: this.__siteId,
        };

        if (this.isChildComponent(component)) {
          analyticsEvent.parentCampaignId = component.parentPublicId;
        }

        this.__analyticsManager
          .sendAnalyticsEvent(analyticsEvent)
          .catch((e) => {
            // eslint-disable-next-line no-console
            console.error(e);
          });

        break;
      }

      case "IMPRESSION": {
        if (!message.documentNodeId) {
          // eslint-disable-next-line no-console
          console.error("[codenbutter] IMPRESSION: Message의 id가 없습니다.");
          return;
        }

        const component =
          this.getComponentByDocumentNodeId(message.documentNodeId) ??
          this.getChildComponentByDocumentNodeId(message.documentNodeId);

        if (!component) {
          // eslint-disable-next-line no-console
          console.error(
            `[codenbutter] IMPRESSION: ${message.id} 에 해당하는 component가 없습니다.`
          );
          return;
        }

        const campaignType = getAnalyticsCampaignsType(
          component.campaignType,
          this.isChildComponent(component)
        );

        if (!campaignType) {
          return;
        }

        const analyticsEvent: AnalyticsEvent = {
          type: "IMPRESSION",
          campaignType,
          campaignId: component.campaignPublicId,
          siteId: this.__siteId,
        };

        // 하위 캠페인일 경우 상위 캠페인 퍼블릭 아이디를 같이 보낸다.
        if (this.isChildComponent(component)) {
          analyticsEvent.parentCampaignId = component.parentPublicId;
        }

        // campaign pageview insight 처리
        if (!this.__ignoreTrack) {
          this.__analyticsManager
            .sendAnalyticsEvent(analyticsEvent)
            .catch((e) => {
              // eslint-disable-next-line no-console
              console.error(e);
            });
        }
        break;
      }

      default:
        throw new Error(`${message.type} method is not found`);
    }
  }

  private __setResizeObserver(resizeObserverCallback: ResizeObserverCallback) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    (async () => {
      if ("ResizeObserver" in window === false) {
        // eslint-disable-next-line @next/next/no-assign-module-variable
        const module = await import(
          /* webpackChunkName: "resize-observer" */ "@juggle/resize-observer"
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).ResizeObserver = module.ResizeObserver;
      }
    })();

    this.__resizeObserver = new ResizeObserver(resizeObserverCallback);
    this.__resizeObserver.observe(document.body);
  }

  private __setResizeHandler(resizeHandler: (e?: Event) => void) {
    window.addEventListener("resize", resizeHandler, { passive: true });
  }

  private __notifyZoomValue() {
    this.__components.forEach((component) => {
      const device = getDevice();
      if (device === "MOBILE") {
        return;
      }

      const zoomValue = getZoomValue(component);
      this.send(component.id, {
        type: "SET_ZOOM",
        value: zoomValue,
      });
    });
  }

  private __polyfillVdh() {
    const vh = window.innerHeight * 0.01;
    this.__components.forEach((component) => {
      if (component.container.style.getPropertyValue("--vh") === `${vh}px`) {
        return;
      }

      component.container.style.setProperty("--vh", `${vh}px`);
    });
  }

  /**
   * id 기반으로 찾습니다.
   * @param id 컴포넌트 id
   */
  getComponentById(id: string): IComponent | undefined {
    return this.getComponents().find((c) => c.id === id);
  }

  getComponentByDocumentNodeId(documentNodeId: string): IComponent | undefined {
    return this.getComponents().find(
      (c) => c.documentNode.id === documentNodeId
    );
  }

  getChildComponentById(id: string): IChildComponent | undefined {
    return this.getChildComponents().find((c) => c.id === id);
  }

  getChildComponentByDocumentNodeId(
    documentNodeId: string
  ): IChildComponent | undefined {
    return this.getChildComponents().find(
      (c) => c.documentNode.id === documentNodeId
    );
  }

  private __removeComponent(id: string) {
    const index = this.getComponents().findIndex((c) => c.id === id);
    if (index === -1) {
      return;
    }
    const component = this.getComponents()[index];
    if (!component) {
      return;
    }
    component.reset();
    this.__components.splice(index, 1);
  }

  private __sendUnsentEvent(component: IComponent) {
    while (component.__eventQueue.length) {
      const message = component.__eventQueue.shift();
      const iframeWindow = component.iframe.contentWindow;

      if (message && iframeWindow) {
        this.__sendMessage(iframeWindow, { id: component.id, ...message });
      }
    }
  }

  private __isOriginAllowed(origin: string): boolean {
    return [window.location.origin, ...this.__allowOrigin].includes(origin);
  }

  createChildComponent({
    id,
    campaignPublicId,
    parentPublicId,
    campaignType,
    documentNode,
    slot,
  }: {
    id: string;
    campaignPublicId: string;
    parentPublicId: string;
    campaignType: CampaignType;
    documentNode: PartialNode<IDocumentNode>;
    slot: number;
  }): IChildComponent {
    const result: IChildComponent = {
      id,
      campaignType,
      campaignPublicId,
      parentPublicId,
      documentNode,
      slot,
    };

    this.__childComponents.push(result);

    return result;
  }

  /**
   * Create Component
   * @param id - component id
   * @param src - iframe url
   * @param renderOptions
   * @param options
   */
  createComponent({
    documentNode,
    campaignPublicId,
    id,
    options = {
      size: { type: "DEFAULT", width: "100px", height: "100px" },
    },
    src,
    trigger,
    frequencyCapping,
    campaignType,
    noCloseForSeconds,
  }: {
    id: string;
    campaignPublicId: string;
    src: string;
    documentNode: PartialNode<IDocumentNode>;
    options?: IComponentOption;
    trigger: ICampaignTriggerEvent[];
    frequencyCapping?: ICampaignFrequencyCappingHandler | null;
    campaignType: CampaignType;
    noCloseForSeconds?: boolean;
  }): IComponent {
    const targetDom = options.target
      ? document.querySelector(options.target)
      : document.body;
    if (!targetDom) {
      throw new Error("target is not found");
    }

    const { container, wrapper, iframe } = createComponentDom({
      id,
      src,
      iframeClassName: options.className,
    });

    // 기본 스타일 적용
    // @see https://app.asana.com/1/1204587038362639/project/1209164678105494/task/1211040172392206
    container.style.width = options.containerStyle?.width || "100%";
    container.style.pointerEvents =
      options.containerStyle?.pointerEvents || "none";
    if (options.style?.width) {
      wrapper.style.width = options.style.width;
      iframe.style.width = "100%";
    }

    // 애니메이션 css 스타일 추가
    if (options.transition && options.transition.active) {
      const style = createAnimationStyle({
        id,
        transition: options.transition,
      });
      const head = document.head || document.getElementsByTagName("head")[0];
      head.appendChild(style);
    }

    targetDom.appendChild(container);

    let backdrop: HTMLDivElement | null = null;

    if (options.backdropStyle) {
      // backdrop은 첫 번째 paint 요소만 가짐.
      const paint = options.backdropStyle[0];
      if (paint && paint.visible !== false) {
        backdrop = createBackdrop({
          backgrounds: options.backdropStyle,
          zIndex:
            documentNode.zIndex ??
            getZIndex({
              group: "sdkJsBody",
              name: "component",
            }),
        });

        if (documentNode.backdropClickAction?.type === "CLOSE") {
          backdrop.onclick = () => {
            this.__handleMessage({
              id,
              type: "CLOSE",
            });
          };
        }
      }
    }

    // 정보들을 가지고 있다가 "LOAD" 등의 이벤트 발생시 스타일/애니메이션 등 적용
    const result: IComponent = {
      id,
      iframe,
      targetDom,
      wrapper,
      container,
      backdrop,
      status: "INITIALIZING",
      options,
      documentNode,
      campaignType,
      campaignPublicId,
      triggered: false,
      wrapperStyle: { ...options.style },
      containerStyle: { ...options.containerStyle },
      __eventQueue: [],
      trigger,
      frequencyCapping,
      reset: () => {
        result.iframe.remove();
        result.container.remove();
        result.wrapper.remove();
        result.backdrop?.remove();
        result.trigger.forEach((t) => {
          t.removeTriggerCallback();
        });
        // TODO: 기존에 이미 상단 여백이 있는 경우 고려
        if (result.hasBodyMarginTop) {
          document.body.style.marginTop = "";
        }
      },
      remove: () => {
        this.__removeComponent(id);
      },
      noCloseForSeconds,
    };

    this.__components.push(result);

    return result;
  }

  send(id: string, message: Message): void {
    const recipientComponent = this.getComponents().find((c) => c.id === id);

    if (!recipientComponent) {
      if (id) {
        // eslint-disable-next-line no-console
        console.error("component not found", { id, message });
      }
      return;
    }

    if (recipientComponent.status === "INITIALIZING") {
      recipientComponent.__eventQueue.push(message);
      return;
    }

    const iframeWindow = recipientComponent.iframe?.contentWindow;
    if (!iframeWindow) {
      // eslint-disable-next-line no-console
      console.error("iframe not found!", { recipientComponent, id, message });
      return;
    }

    this.__sendMessage(iframeWindow, { ...message, id: recipientComponent.id });
  }

  /**
   * Get all components. 반환된 component 를 변경하면 원본 component 도 변경됩니다.
   * @returns components
   */
  getComponents(): IComponent[] {
    return [...this.__components];
  }

  getChildComponents(): IChildComponent[] {
    return [...this.__childComponents];
  }

  /**
   * 입력 받은 component가 자식 component인지 childComponent인지 boolean 값을 반환한다.
   * @param component
   * @returns
   */
  isChildComponent(
    component: IChildComponent | IComponent
  ): component is IChildComponent {
    return (component as IChildComponent).parentPublicId !== undefined;
  }

  /**
   * 시지를 허용할 iframe origin 추가
   * @param origin origin (url)
   */
  addAllowOrigin(origin: string): void {
    this.__allowOrigin.push(origin);
  }

  /**
   * 등록된 allow origin을 조회
   * @returns allow origins
   */
  getAllowOrigins(): string[] {
    return this.__allowOrigin;
  }

  /**
   * Manager 제거
   * 관리하는 컴포넌트도 제거함
   */
  cleanUp(): void {
    if (this.__bindedReceiveMessage) {
      window.removeEventListener("message", this.__bindedReceiveMessage, false);
    }
    if (this.__unsubscribeStores) {
      this.__unsubscribeStores();
      this.__unsubscribeStores = undefined;
    }
    this.removeAllComponents();
    this.__resizeObserver?.disconnect();
    this.__allowOrigin = [];
  }

  removeAllComponents(): void {
    this.getComponents().forEach((c) => {
      c.reset();
    });

    this.__components = [];
    this.__childComponents = [];
  }

  setPosition(
    componentId: string,
    actualSize: { width: number; height: number }
  ): void {
    const component = this.getComponents().find((c) => c.id === componentId);
    if (!component) {
      return;
    }

    const { horizontalProperty, verticalProperty } = getPositionStyle({
      actualSize,
      document: component.documentNode,
    });

    component.wrapper.style.removeProperty("top");
    component.wrapper.style.removeProperty("bottom");
    component.wrapper.style.removeProperty("left");
    component.wrapper.style.removeProperty("right");
    component.wrapper.style[horizontalProperty.cssPropertyName] =
      horizontalProperty.value;
    component.wrapper.style[verticalProperty.cssPropertyName] =
      verticalProperty.value;
  }
}

export default Manager;
